// printers/BasePrinter.ts
export interface PrinterConnectionResult {
  ip: string;
  type: string;
  success: boolean;
  message: string;
}

export abstract class BasePrinter {
  protected readonly timeoutMs = 5000;
  abstract readonly type: string;

  abstract sendGcodeToPrinter(
    designTitle: string,
    gcodeText: string,
    ip: string
  ): Promise<void>;

  abstract checkPrinterConnection(
    ip: string
  ): Promise<PrinterConnectionResult>;

  protected getPrinterNetworkErrorMessage(error: unknown): string {
    if (error instanceof Error && error.name === 'AbortError') {
      return `Printer did not respond in time (${this.timeoutMs}ms). Check the printer IP address and ensure the printer is reachable on the local network.`;
    }

    if (error instanceof TypeError) {
      return 'Unable to reach the printer. Check the IP address, local network connection, and whether the printer is online.';
    }

    return 'Printer request failed. Check the printer connection and try again.';
  }

  protected async fetchWithTimeout(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
        cache: 'no-store',
      });
    } catch (error) {
      throw new Error(this.getPrinterNetworkErrorMessage(error));
    } finally {
      clearTimeout(id);
    }
  }
}
